import { useState, useEffect, KeyboardEvent, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import { ArrowRight, CalendarIcon, Search, ShieldOff, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const EXCLUDE_STORAGE_KEY = "unmapped_sessions_exclude";

// ─── Exclude popover ──────────────────────────────────────────────────────────

interface ExcludePopoverProps {
  tags: string[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onRemove: (tag: string) => void;
}

function ExcludePopover({
  tags,
  inputValue,
  onInputChange,
  onKeyDown,
  onBlur,
  onRemove,
}: ExcludePopoverProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 gap-2 font-normal",
            tags.length > 0 ? "text-foreground" : "text-muted-foreground"
          )}
        >
          <ShieldOff className="h-4 w-4 shrink-0" />
          {tags.length === 0 ? (
            <span>Exclude domain or email</span>
          ) : (
            <span>
              Excluding{" "}
              <Badge
                variant="secondary"
                className="ml-1 px-1.5 py-0 h-4 text-[11px]"
              >
                {tags.length}
              </Badge>
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-80 p-3"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <p className="text-xs text-muted-foreground mb-2">
          Press <kbd className="font-mono bg-muted px-1 rounded">Enter</kbd> or{" "}
          <kbd className="font-mono bg-muted px-1 rounded">,</kbd> to add a
          domain or full email. Backspace removes the last tag.
        </p>

        <div
          className={cn(
            "flex flex-wrap gap-1.5 min-h-[36px] px-2 py-1.5 rounded-md border border-input bg-background",
            "focus-within:ring-1 focus-within:ring-ring"
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full"
            >
              {tag}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(tag);
                }}
                className="text-gray-400 hover:text-gray-700 leading-none"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            placeholder={tags.length === 0 ? "rockethealth.app, user@mail.com…" : ""}
            className="flex-1 min-w-[140px] bg-transparent outline-none text-xs py-0.5 placeholder:text-muted-foreground"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function UnmappedSessionsFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || ""
  );

  // Exclusion tags — persisted as comma-joined in URL + localStorage
  const currentExclude = searchParams.get("exclude") || "";
  const excludeTags = currentExclude
    ? currentExclude.split(",").map((t) => t.trim()).filter(Boolean)
    : [];
  const [excludeInput, setExcludeInput] = useState("");

  // On mount: if URL has no exclude param, restore from localStorage
  useEffect(() => {
    if (!searchParams.get("exclude")) {
      const stored = localStorage.getItem(EXCLUDE_STORAGE_KEY);
      if (stored) {
        const params = new URLSearchParams(searchParams);
        params.set("exclude", stored);
        setSearchParams(params, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateFrom = searchParams.get("date_from");
  const dateTo = searchParams.get("date_to");

  const form = useForm({
    defaultValues: {
      dateRange: {
        start: dateFrom ? new Date(dateFrom) : null,
        end: dateTo ? new Date(dateTo) : null,
      },
    },
  });

  const hasActiveFilters =
    !!searchParams.get("search") ||
    excludeTags.length > 0 ||
    !!dateFrom ||
    !!dateTo;

  const handleFilterChange = (updates: Record<string, unknown>) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "0");

    Object.entries(updates).forEach(([key, value]) => {
      if (key === "dateRange") {
        const dr = value as { start: Date | null; end: Date | null };
        if (dr.start && dr.end) {
          params.set("date_from", dr.start.toISOString());
          const end = new Date(dr.end);
          end.setHours(23, 59, 59, 999);
          params.set("date_to", end.toISOString());
        } else {
          params.delete("date_from");
          params.delete("date_to");
        }
      }
    });

    setSearchParams(params);
  };

  const commitSearch = () => {
    const params = new URLSearchParams(searchParams);
    if (searchInput.trim()) {
      params.set("search", searchInput.trim());
    } else {
      params.delete("search");
    }
    params.set("page", "0");
    setSearchParams(params);
  };

  const addExcludeTag = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || excludeTags.includes(trimmed)) return;
    const next = [...excludeTags, trimmed];
    const params = new URLSearchParams(searchParams);
    params.set("exclude", next.join(","));
    params.set("page", "0");
    setSearchParams(params);
    localStorage.setItem(EXCLUDE_STORAGE_KEY, next.join(","));
    setExcludeInput("");
  };

  const removeExcludeTag = (tag: string) => {
    const next = excludeTags.filter((t) => t !== tag);
    const params = new URLSearchParams(searchParams);
    if (next.length > 0) {
      params.set("exclude", next.join(","));
      localStorage.setItem(EXCLUDE_STORAGE_KEY, next.join(","));
    } else {
      params.delete("exclude");
      localStorage.removeItem(EXCLUDE_STORAGE_KEY);
    }
    params.set("page", "0");
    setSearchParams(params);
  };

  const handleExcludeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addExcludeTag(excludeInput.replace(/,$/, ""));
    } else if (
      e.key === "Backspace" &&
      excludeInput === "" &&
      excludeTags.length > 0
    ) {
      removeExcludeTag(excludeTags[excludeTags.length - 1]);
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setExcludeInput("");
    localStorage.removeItem(EXCLUDE_STORAGE_KEY);
    form.reset({ dateRange: { start: null, end: null } });
    const params = new URLSearchParams();
    params.set("page", "0");
    setSearchParams(params);
  };

  return (
    <Form {...form}>
      <div className="flex flex-wrap items-start gap-3">
        {/* Search */}
        <div className="flex items-center gap-1">
          <Input
            placeholder="Search by name, phone, or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && commitSearch()}
            className="w-64 h-9 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={commitSearch}
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>

        {/* Date range — Popover + Calendar (same as bundle discrepancy) */}
        <FormField
          control={form.control}
          name="dateRange"
          render={({ field }) => (
            <FormItem>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={`w-[200px] justify-start text-left font-normal bg-white text-gray-900 border-gray-300 hover:bg-gray-50 ${
                        !field.value.start && "text-gray-500"
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value.start && field.value.end ? (
                        <>
                          {format(field.value.start, "LLL dd, y")} –{" "}
                          {format(field.value.end, "LLL dd, y")}
                        </>
                      ) : (
                        <span>Session date range</span>
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  {/* Display boxes */}
                  <div className="flex items-end gap-3 mb-4 px-4">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        Start
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.start ? (
                          format(field.value.start, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1.5 block">
                        End
                      </label>
                      <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                        {field.value.end ? (
                          format(field.value.end, "LLL dd, y")
                        ) : (
                          <span className="text-muted-foreground">Select</span>
                        )}
                      </div>
                    </div>
                    {(field.value.start || field.value.end) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const cleared = { start: null, end: null };
                          field.onChange(cleared);
                          handleFilterChange({ dateRange: cleared });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <Calendar
                    initialFocus
                    mode="range"
                    selected={{
                      from: field.value.start || undefined,
                      to: field.value.end || undefined,
                    }}
                    onSelect={(range) => {
                      const newValue = {
                        start: range?.from || null,
                        end: range?.to || null,
                      };
                      field.onChange(newValue);
                      if (range?.from && range?.to) {
                        handleFilterChange({ dateRange: newValue });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </FormItem>
          )}
        />

        {/* Exclusion tag input — fixed-height trigger, chips live inside popover */}
        <ExcludePopover
          tags={excludeTags}
          inputValue={excludeInput}
          onInputChange={setExcludeInput}
          onKeyDown={handleExcludeKeyDown}
          onBlur={() => excludeInput.trim() && addExcludeTag(excludeInput)}
          onRemove={removeExcludeTag}
        />

        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={handleReset}>
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>
    </Form>
  );
}
